/* eslint-disable no-param-reassign */
import { createAction, createReducer } from '@reduxjs/toolkit'

import { USE_REDUX_TOOLS } from 'system'

import cloneDeep from 'lodash/cloneDeep'

import type { Draft, PayloadActionCreator } from '@reduxjs/toolkit'

const reduxFetchCreatorReduxTools = <
  TName extends string,
  TPayload,
  TState extends { loaded?: boolean; loading?: boolean } & NonNullable<unknown>,
  TPayloadFulfilled = TState,
  TPayloadError = TPayloadFulfilled,
  TPayloadCancel = void,
>(
  actionsPrefix: TName,
  initialState: TState,
  actions?: {
    cancel?: (
      state: TState,
      action: {
        payload: { originalAction?: { payload: TPayload; type: TName } } & TPayloadCancel
        type: `${TName}Cancel`
      },
    ) => TState | void
    clear?: (state: TState, action: { payload?: TPayload; type: `${TName}Clear` }) => TState | void
    error?: (
      state: TState,
      action: {
        payload: { originalAction?: { payload: TPayload; type: TName } } & TPayloadError
        type: `${TName}Error`
      },
    ) => TState | void
    fulfilled?: (
      state: TState,
      action: {
        payload: { originalAction?: { payload: TPayload; type: TName } } & TPayloadFulfilled
        type: `${TName}Fulfilled`
      },
    ) => TState | void
    start?: (state: TState, action: { payload: TPayload; type: TName }) => TState | void
  },
) => {
  const actionStart = createAction<TPayload, TName>(actionsPrefix)
  const actionClear = createAction<TPayload, `${TName}Clear`>(`${actionsPrefix}Clear`)
  const actionFulfilled = createAction<
    { originalAction?: { payload: TPayload; type: TName } } & TPayloadFulfilled,
    `${TName}Fulfilled`
  >(`${actionsPrefix}Fulfilled`)
  const actionError = createAction<
    { originalAction?: { payload: TPayload; type: TName } } & TPayloadError,
    `${TName}Error`
  >(`${actionsPrefix}Error`)
  const actionCancel = createAction<TPayloadCancel, `${TName}Cancel`>(`${actionsPrefix}Cancel`)

  const reducer = createReducer(initialState, (builder) =>
    builder
      .addCase(actionStart, (state, action) => {
        if (actions?.start) {
          state =
            (actions.start(state as TState, action as { payload: TPayload; type: TName }) as Draft<TState>) || state
        }

        state.loading = true

        state.loaded = false
        return state
      })
      .addCase(actionClear, (state, action) => {
        if (actions?.clear) {
          state =
            (actions.clear(
              state as TState,
              action as {
                payload?: TPayload
                type: `${TName}Clear`
              },
            ) as Draft<TState>) || state
        }

        state.loading = false
        state.loaded = false
        return state
      })
      .addCase(actionFulfilled, (state, action) => {
        if (actions?.fulfilled) {
          state =
            (actions.fulfilled(
              state as TState,
              action as {
                payload: { originalAction?: { payload: TPayload; type: TName } } & TPayloadFulfilled
                type: `${TName}Fulfilled`
              },
            ) as Draft<TState>) || state
        }

        state.loading = false

        state.loaded = true
        return state
      })
      .addCase(actionError, (state, action) => {
        if (actions?.error) {
          state =
            (actions.error(
              state as TState,
              action as {
                payload: { originalAction?: { payload: TPayload; type: TName } } & TPayloadError
                type: `${TName}Error`
              },
            ) as Draft<TState>) || state
        }

        state.loading = false

        state.loaded = true
        return state
      })
      .addCase(actionCancel, (state, action) => {
        if (actions?.cancel) {
          state =
            (actions.cancel(
              state as TState,
              action as {
                payload: { originalAction?: { payload: TPayload; type: TName } } & TPayloadCancel
                type: `${TName}Cancel`
              },
            ) as Draft<TState>) || state
        }

        state.loading = false
        return state
      }),
  )

  return {
    actions: {
      [`${actionsPrefix}Cancel`]: actionCancel,
      [`${actionsPrefix}Clear`]: actionClear,
      [`${actionsPrefix}Error`]: actionError,
      [`${actionsPrefix}Fulfilled`]: actionFulfilled,
      [actionsPrefix]: actionStart,
    },
    reducer,
  } as {
    actions: Record<`${TName}Cancel`, typeof actionCancel> &
      Record<`${TName}Clear`, typeof actionClear> &
      Record<`${TName}Error`, typeof actionError> &
      Record<`${TName}Fulfilled`, typeof actionFulfilled> &
      Record<TName, typeof actionStart>
  } & Record<'reducer', typeof reducer>
}

export const actionCreator = <TPayload = void, TName extends string = string>(
  type: TName,
): PayloadActionCreator<TPayload, TName> => {
  const action = (payload: TPayload) => ({
    payload,
    type,
  })

  action.toString = () => `${type}`
  action.type = type
  action.match = (act: { type: string }): boolean => type === act.type

  return action as PayloadActionCreator<TPayload, TName>
}

const reduxFetchCreatorRedux = <
  TName extends string,
  TPayload,
  TState extends { loaded?: boolean; loading?: boolean } & NonNullable<unknown>,
  TPayloadFulfilled = TState,
  TPayloadError = TPayloadFulfilled,
  TPayloadCancel = void,
>(
  actionsPrefix: TName,
  initialState: TState,
  actions?: {
    cancel?: (
      state: TState,
      action: {
        payload: { originalAction?: { payload: TPayload; type: TName } } & TPayloadCancel
        type: `${TName}Cancel`
      },
    ) => TState | void
    clear?: (state: TState, action: { payload?: TPayload; type: `${TName}Clear` }) => TState | void
    error?: (
      state: TState,
      action: {
        payload: { originalAction?: { payload: TPayload; type: TName } } & TPayloadError
        type: `${TName}Error`
      },
    ) => TState | void
    fulfilled?: (
      state: TState,
      action: {
        payload: { originalAction?: { payload: TPayload; type: TName } } & TPayloadFulfilled
        type: `${TName}Fulfilled`
      },
    ) => TState | void
    start?: (state: TState, action: { payload: TPayload; type: TName }) => TState | void
  },
) => {
  const actionStart = actionCreator<TPayload, TName>(actionsPrefix)
  const actionClear = actionCreator<TPayload, `${TName}Clear`>(`${actionsPrefix}Clear`)
  const actionFulfilled = actionCreator<
    { originalAction?: { payload: TPayload; type: TName } } & TPayloadFulfilled,
    `${TName}Fulfilled`
  >(`${actionsPrefix}Fulfilled`)
  const actionError = actionCreator<
    { originalAction?: { payload: TPayload; type: TName } } & TPayloadError,
    `${TName}Error`
  >(`${actionsPrefix}Error`)
  const actionCancel = actionCreator<TPayloadCancel, `${TName}Cancel`>(`${actionsPrefix}Cancel`)

  const reducer = (
    state = initialState,
    action: {
      payload?:
        | ({ originalAction?: { payload: TPayload; type: TName } } & TPayloadCancel)
        | ({ originalAction?: { payload: TPayload; type: TName } } & TPayloadError)
        | ({ originalAction?: { payload: TPayload; type: TName } } & TPayloadFulfilled)
        | TPayload
      type?: `${TName}Cancel` | `${TName}Clear` | `${TName}Error` | `${TName}Fulfilled` | TName
    } = {},
  ) => {
    let newState = cloneDeep(state)
    switch (action.type) {
      case actionCancel.type: {
        if (actions?.cancel) {
          newState =
            actions.cancel(
              newState as TState,
              action as {
                payload: { originalAction?: { payload: TPayload; type: TName } } & TPayloadCancel
                type: `${TName}Cancel`
              },
            ) || newState
        }

        newState.loading = false
        return newState
      }
      case actionClear.type: {
        if (actions?.clear) {
          newState =
            actions.clear(
              newState as TState,
              action as {
                payload?: TPayload
                type: `${TName}Clear`
              },
            ) || newState
        }

        newState.loading = false
        newState.loaded = false
        return newState
      }
      case actionError.type: {
        newState = actions?.error
          ? (actions.error(
              newState as TState,
              action as {
                payload: { originalAction?: { payload: TPayload; type: TName } } & TPayloadError
                type: `${TName}Error`
              },
            ) ?? newState)
          : { ...newState, ...action.payload }

        newState.loading = false
        newState.loaded = true
        return newState
      }
      case actionFulfilled.type: {
        newState = actions?.fulfilled
          ? (actions.fulfilled(
              newState,
              action as {
                payload: { originalAction?: { payload: TPayload; type: TName } } & TPayloadFulfilled
                type: `${TName}Fulfilled`
              },
            ) ?? newState)
          : { ...newState, ...action.payload }

        newState.loading = false
        newState.loaded = true
        return newState
      }
      case actionStart.type: {
        if (actions?.start) {
          newState = actions.start(newState as TState, action as { payload: TPayload; type: TName }) || newState
        }

        newState.loading = true
        newState.loaded = false
        return newState
      }
      default: {
        return state
      }
    }
  }

  return {
    actions: {
      [`${actionsPrefix}Cancel`]: actionCancel,
      [`${actionsPrefix}Clear`]: actionClear,
      [`${actionsPrefix}Error`]: actionError,
      [`${actionsPrefix}Fulfilled`]: actionFulfilled,
      [actionsPrefix]: actionStart,
    },
    reducer,
  } as {
    actions: Record<`${TName}Cancel`, typeof actionCancel> &
      Record<`${TName}Clear`, typeof actionClear> &
      Record<`${TName}Error`, typeof actionError> &
      Record<`${TName}Fulfilled`, typeof actionFulfilled> &
      Record<TName, typeof actionStart>
  } & Record<'reducer', typeof reducer>
}

const reduxFetchCreator = USE_REDUX_TOOLS ? reduxFetchCreatorReduxTools : reduxFetchCreatorRedux

export default reduxFetchCreator
